import type {
  AccommodationAllocationInput,
  AccommodationAvailableRoomsInput,
  AccommodationRoomFacilityType,
} from '@data-access/graphql/schema.types'
import {
  AccommodationAvailableRoomsDocument,
  type AccommodationAvailableRoomsQuery,
  type AccommodationAvailableRoomsQueryVariables,
} from '@data-access/lib/gql/accommodations/documents'
import { useGraphQL } from '@data-access/lib/graphql-query'
import { formatIsoDate } from '@utils/utils-format'

import { isNotNil } from 'es-toolkit'

import { getLowestRate } from '../utils/get-lowest-rate'

export type AvailableRoom = Required<ReturnType<typeof select>[number]>
type AvailableRoomsConfig = {
  accommodationId: number
  from?: Date | null
  to?: Date | null
  allocation: AccommodationAllocationInput[]
}

export const useAvailableRooms = (
  input: AccommodationAvailableRoomsInput | null,
  options?: Parameters<
    typeof useGraphQL<AccommodationAvailableRoomsQuery, AccommodationAvailableRoomsQueryVariables>
  >[2]
) => {
  const query = useGraphQL(
    AccommodationAvailableRoomsDocument,
    { input: input! },
    {
      ...options,
      select: data => select(data) as AvailableRoom[],
      enabled: !!input,
    }
  )

  return { ...query, input }
}

export const getInput = ({ accommodationId, from, to, allocation }: AvailableRoomsConfig) => {
  if (!from || !to) return null

  return {
    id: accommodationId,
    checkin: formatIsoDate(from),
    checkout: formatIsoDate(to),
    allocation,
  }
}

const select = ({ availableRooms }: AccommodationAvailableRoomsQuery) =>
  availableRooms.rooms.map(room => ({
    ...room,
    facilityHighlights: filterFacilityHighlights(room.facilityTypes),
    lowestRate: getLowestRate(room.rates).pricing,
    images: room.images.map(image => ({
      url: image.url,
      alt: room.title,
    })),
  }))

export function filterFacilityHighlights(facilityTypes: AccommodationRoomFacilityType[]) {
  return facilityTypes
    .filter(facilityType => facilityType.id === 8)
    .flatMap(facilityType => facilityType.facilities)
    .filter(isNotNil)
}
